"use client";

import Link from "next/link";
import Image from "next/image";
import { useLocale } from "next-intl";
import { FavoriteButton } from "@/ui/components/favorite-button";
import type { ProductCardData } from "@/ui/components/plp/product-card";
import type { PeptideMetadata } from "@/lib/peptide-metadata";
import {
	translateAvailability,
	translatePeptideCategory,
	translateResearchLevel,
	translateTrustBadge,
} from "@/lib/peptide-translations";
import { defaultLocale, formatPrice, isValidLocale } from "@/config/locale";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";

export interface PeptideProductCardData extends ProductCardData {
	peptideMetadata: PeptideMetadata | null;
}

interface PeptideProductCardProps {
	product: PeptideProductCardData;
	priority?: boolean;
}

export function PeptideProductCard({ product, priority = false }: PeptideProductCardProps) {
	const locale = useLocale();
	const pm = product.peptideMetadata;
	const pres = product.pricePresentation;
	const ploc = product.locale;
	const pch = product.channel;
	const enriched = pres != null && ploc != null && pch != null;
	const fallbackLocale = isValidLocale(locale) ? locale : defaultLocale;

	return (
		<article className="hover:border-primary/30 group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-all duration-300 hover:shadow-md">
			{/* Favorite */}
			<div
				className="absolute right-2.5 top-2.5 z-10"
				onClick={(e) => e.preventDefault()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<FavoriteButton
					slug={product.slug}
					className="bg-background/80 backdrop-blur-sm hover:bg-background"
				/>
			</div>

			<Link href={product.href} className="flex flex-1 flex-col">
				{/* Image */}
				<div className="relative aspect-square overflow-hidden bg-secondary">
					<Image
						src={product.image}
						alt={product.imageAlt || product.name}
						fill
						sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
						className="object-contain object-center p-3 transition-transform duration-500 ease-out group-hover:scale-[1.04]"
						priority={priority}
					/>

					{/* Floating badges — top-left, stacked */}
					{pm && (pm.availability || pm.researchLevel) && (
						<div className="absolute left-0 top-2.5 flex max-w-[min(100%,11rem)] flex-col gap-0.5 pr-1">
							{pm.availability && (
								<span className="bg-background/85 block truncate rounded-r-full border border-border px-1.5 py-px text-[10px] font-semibold tracking-wide text-foreground shadow-sm backdrop-blur-sm">
									{translateAvailability(pm.availability, locale)}
								</span>
							)}
							{pm.researchLevel && (
								<span className="block truncate rounded-r-full border border-info-border bg-info px-1.5 py-px text-[10px] font-medium tracking-wide text-info-foreground backdrop-blur-sm">
									{translateResearchLevel(pm.researchLevel, locale)}
								</span>
							)}
						</div>
					)}

					{/* Score — bottom-right floating */}
					{pm && (
						<div className="absolute bottom-2.5 right-2.5">
							<span className="bg-background/80 inline-flex items-center gap-1 rounded-full border border-white/25 px-2 py-0.5 font-mono text-[11px] font-bold tabular-nums text-foreground shadow-sm backdrop-blur-sm">
								<span className="h-1.5 w-1.5 rounded-full bg-primary" />
								{pm.scores.overall.toFixed(1)}
							</span>
						</div>
					)}
				</div>

				{/* Info */}
				<div className="flex flex-1 flex-col gap-1.5 p-3.5 pt-3">
					{/* Brand */}
					{product.brand && (
						<p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
							{product.brand}
						</p>
					)}

					{/* Name */}
					<h3 className="line-clamp-2 text-[15px] font-semibold leading-snug tracking-tight group-hover:text-primary">
						{product.name}
					</h3>

					{/* Description */}
					{product.shortDescription && (
						<p className="line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">
							{product.shortDescription}
						</p>
					)}

					{/* Review summary */}
					{product.reviewSummary && product.reviewSummary.count > 0 && (
						<div className="flex items-center gap-1">
							<svg
								className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
								viewBox="0 0 20 20"
								aria-hidden="true"
							>
								<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
							</svg>
							<span className="text-xs font-medium tabular-nums text-foreground">
								{product.reviewSummary.avgRating.toFixed(1)}
							</span>
							<span className="text-xs text-muted-foreground">({product.reviewSummary.count})</span>
						</div>
					)}

					{/* Tags row — categories + trust in a single line */}
					{(pm?.peptideCategories.length || product.trustBadges?.length) && (
						<div className="mt-auto flex flex-wrap items-center gap-1 pt-2">
							{pm?.peptideCategories.slice(0, 2).map((cat) => (
								<span
									key={cat}
									className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
								>
									{translatePeptideCategory(cat, locale)}
								</span>
							))}
							{product.trustBadges &&
								product.trustBadges.length > 0 &&
								pm?.peptideCategories.length !== undefined &&
								pm.peptideCategories.length > 0 && <span className="mx-0.5 h-3 w-px bg-border" />}
							{product.trustBadges?.slice(0, 2).map((badge) => (
								<span
									key={badge}
									className="bg-success/10 rounded px-1.5 py-0.5 text-[10px] font-medium text-success"
								>
									{translateTrustBadge(badge, locale)}
								</span>
							))}
						</div>
					)}

					{/* Price */}
					<div className="mt-auto flex items-baseline gap-2 pt-2">
						{enriched ? (
							<span className="text-base font-bold tabular-nums tracking-tight">
								<ReferencePrimaryPrice presentation={pres} locale={ploc} channel={pch} size="md" />
							</span>
						) : (
							<span className="text-base font-bold tabular-nums tracking-tight">
								{formatPrice(product.price, product.currency, fallbackLocale)}
							</span>
						)}
						{product.compareAtPrice && enriched && product.compareAtPresentation ? (
							<span className="text-[13px] tabular-nums text-muted-foreground line-through">
								<ReferencePrimaryPrice
									presentation={product.compareAtPresentation}
									locale={ploc}
									channel={pch}
									size="sm"
									className="text-[13px] font-normal text-muted-foreground line-through"
								/>
							</span>
						) : product.compareAtPrice ? (
							<span className="text-[13px] tabular-nums text-muted-foreground line-through">
								{formatPrice(product.compareAtPrice, product.currency, fallbackLocale)}
							</span>
						) : null}
					</div>
				</div>
			</Link>
		</article>
	);
}
